/**
 * @file DataControlsSection.tsx
 * @brief Company settings → Data & sharing: keep company meetings inside the
 *        company (ADR-051), plus the one-time notice for admins.
 *
 * @details
 * DES 21-10-2026. Three switches, the server's defaults:
 * - Only share inside the company (on): members share meetings, notes and
 *   to-dos with colleagues only. Guests can still be in a meeting and named.
 * - Admins too (off): admins also bound; shown under the first switch, only
 *   while it is on.
 * - Allow PDF and text export (on): off = admins only.
 * Content already shared outside stays until the admin removes it here
 * ("Remove all external shares"); nothing is removed silently. Every change
 * is written to the activity log by the server. Copy to clipboard is not
 * offered: it cannot be enforced on every surface (brief item 3).
 *
 * Layout: the GridBand + row pattern of CompanyDataSharingSwitch.
 *
 * @references
 * - scratch/claude-code-report.md "Company data controls: Part 1" section 1
 * - ./api.ts
 *
 * @author DES
 * @date 21-10-2026
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { Info, LoaderCircle, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Switch } from "@/components/ui/Switch";
import { ApiClientError } from "@/shared/api/client";
import { toast } from "@/shared/hooks/useToast";
import { cn } from "@/shared/theme/cn";
import {
  useCompanyDataControls,
  useExternalShares,
  useRemoveAllExternalShares,
  useSaveCompanyDataControls,
  type DataControlsPatch,
} from "./api";

const ROW = "flex items-start gap-3 border-b border-border/60 px-4 py-3.5 md:px-5";

export const DATA_CONTROLS_NOTICE =
  "We now keep your company's meetings inside the company. You can change this in Data & sharing.";

function errorText(err: unknown) {
  return err instanceof ApiClientError ? err.message : "Please try again.";
}

function SwitchRow({
  title,
  body,
  checked,
  disabled,
  onChange,
  indent = false,
}: {
  title: string;
  body: string;
  checked: boolean;
  disabled: boolean;
  onChange: (v: boolean) => void;
  indent?: boolean;
}) {
  return (
    <div className={ROW}>
      <span className={indent ? "min-w-0 flex-1 pl-4" : "min-w-0 flex-1"}>
        <span className="block text-sm font-medium">{title}</span>
        <span className="block text-xs text-muted-foreground">{body}</span>
      </span>
      <Switch
        checked={checked}
        disabled={disabled}
        onCheckedChange={(v: boolean) => onChange(v)}
        aria-label={title}
        className="mt-0.5"
      />
    </div>
  );
}

/** The one-time notice (`noticePending`), for admins. "Got it" marks it seen. */
export function DataControlsNotice({
  linkToSettings = false,
  className,
}: {
  linkToSettings?: boolean;
  className?: string;
}) {
  const q = useCompanyDataControls();
  const save = useSaveCompanyDataControls();
  if (!q.data?.dataControls.noticePending || !q.data.enforced) return null;
  return (
    <div className={cn("flex items-start gap-3 rounded-2xl bg-brand/[0.06] px-4 py-3 text-sm", className)}>
      <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-brand" aria-hidden />
      <span className="min-w-0 flex-1 text-app-text">
        {DATA_CONTROLS_NOTICE}
        {linkToSettings ? (
          <>
            {" "}
            <Link to="/company/settings" className="font-medium text-brand hover:underline">
              Data &amp; sharing
            </Link>
          </>
        ) : null}
      </span>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        disabled={save.isPending}
        onClick={() =>
          save.mutate(
            {},
            { onError: (e) => toast({ variant: "destructive", title: "Could not save", description: errorText(e) }) },
          )
        }
      >
        Got it
      </Button>
    </div>
  );
}

export function DataControlsSection() {
  const q = useCompanyDataControls();
  const save = useSaveCompanyDataControls();
  const [showList, setShowList] = useState(false);
  const count = q.data?.externalShareCount ?? 0;
  const list = useExternalShares(showList && count > 0);
  const removeAll = useRemoveAllExternalShares();

  if (!q.data) {
    return (
      <div className="border-b border-border/60 px-4 py-3.5 text-sm text-muted-foreground md:px-5">
        {q.isError ? "Could not load the sharing rules." : "Loading…"}
      </div>
    );
  }
  const dc = q.data.dataControls;
  const change = (patch: DataControlsPatch) =>
    save.mutate(patch, {
      onError: (e) => toast({ variant: "destructive", title: "Could not save", description: errorText(e) }),
    });

  const onRemoveAll = () => {
    if (
      !window.confirm(
        `Remove all external shares?\n\nPeople outside your company will no longer see the ${count} ${count === 1 ? "meeting or note" : "meetings and notes"} listed here. Owners keep them. This is written to the activity log.`,
      )
    ) {
      return;
    }
    removeAll.mutate(undefined, {
      onSuccess: (res) => {
        const m = res.removed?.meetings ?? 0;
        const n = res.removed?.notes ?? 0;
        toast({
          variant: "success",
          title: "External shares removed",
          description: `${m} meeting${m === 1 ? "" : "s"}, ${n} note${n === 1 ? "" : "s"}.`,
        });
        setShowList(false);
      },
      onError: (e) =>
        toast({ variant: "destructive", title: "Could not remove", description: errorText(e) }),
    });
  };

  return (
    <>
      {dc.noticePending && q.data.enforced ? (
        <div className="border-b border-border/60 px-4 py-3 md:px-5">
          <DataControlsNotice />
        </div>
      ) : null}
      {q.data.enforced ? null : (
        <p className="flex items-start gap-2 border-b border-border/60 px-4 py-3 text-xs text-muted-foreground md:px-5">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          VERBLIZR has paused these rules for now. Your choices are saved and apply again when they
          are back.
        </p>
      )}
      <SwitchRow
        title="Only share inside the company"
        body="Members can share meetings, notes and to-dos only with people in your company. Guests can still join a meeting and be named as speakers, but get no copy."
        checked={dc.shareInsideOnly}
        disabled={save.isPending}
        onChange={(v) => change({ shareInsideOnly: v })}
      />
      {dc.shareInsideOnly ? (
        <SwitchRow
          indent
          title="Admins too"
          body="Company admins also share only inside the company. Off: admins can still share outside, and each time is written to the activity log."
          checked={dc.adminsToo}
          disabled={save.isPending}
          onChange={(v) => change({ adminsToo: v })}
        />
      ) : null}
      <SwitchRow
        title="Allow PDF and text export"
        body="Off: only company admins can export or download meeting PDFs, transcripts and audio. Members see these buttons turned off."
        checked={dc.allowExport}
        disabled={save.isPending}
        onChange={(v) => change({ allowExport: v })}
      />

      {/* Already shared outside: listed, removed only on the admin's word. */}
      {count > 0 ? (
        <div className="border-b border-border/60 px-4 py-3.5 md:px-5">
          <div className="flex flex-wrap items-center gap-3">
            <span className="min-w-0 flex-1 text-sm">
              <span className="block font-medium">
                {count} {count === 1 ? "share" : "shares"} with people outside your company
              </span>
              <span className="block text-xs text-muted-foreground">
                They keep working until you remove them.
              </span>
            </span>
            <Button type="button" size="sm" variant="ghost" onClick={() => setShowList((v) => !v)}>
              {showList ? "Hide" : "Review"}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={removeAll.isPending}
              onClick={onRemoveAll}
            >
              {removeAll.isPending ? <LoaderCircle className="mr-1.5 h-4 w-4 animate-spin" /> : null}
              Remove all external shares
            </Button>
          </div>
          {showList ? (
            <ul className="mt-3 divide-y divide-border/60 text-sm">
              {list.isLoading ? (
                <li className="py-2 text-muted-foreground">Loading…</li>
              ) : (list.data ?? []).length === 0 ? (
                <li className="py-2 text-muted-foreground">None left.</li>
              ) : (
                (list.data ?? []).map((s) => (
                  <li key={`${s.kind}:${s.id}:${s.person?.userId ?? s.person?.email}`} className="py-2">
                    <span className="block truncate font-medium">
                      {s.title?.trim() || (s.kind === "note" ? "Untitled note" : "Untitled meeting")}
                      <span className="ml-2 text-xs font-normal text-muted-foreground">
                        {s.kind === "note" ? "Note" : "Meeting"}
                      </span>
                    </span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {[
                        `With ${s.person?.name || s.person?.email || "someone outside"}`,
                        s.owner?.name ? `owner ${s.owner.name}` : null,
                        s.sharedAt ? format(new Date(s.sharedAt), "d MMM yyyy") : null,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                  </li>
                ))
              )}
            </ul>
          ) : null}
        </div>
      ) : null}
    </>
  );
}
